import Botservice from './Botservice';

const ACCESS_TOKEN_KEY = 'bot_access_token';
const REFRESH_TOKEN_KEY = 'bot_refresh_token';
const BOT_USER_ID_KEY = 'bot_user_id';

interface BotLoginResponse {
  access_token: string;
  refresh_token?: string;
  token_type?: string;
}

interface BotRefreshResponse {
  access_token: string;
  token_type?: string;
}

const buildBotUrl = (path: string) => `${Botservice}${path.replace(/^\//, '')}`;

const parseJsonSafely = async (response: Response) => {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const text = await response.text();
    return text ? { message: text } : null;
  }

  try {
    return await response.json();
  } catch {
    return null;
  }
};

const getErrorMessage = (data: any, fallback: string) => {
  if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail;
  if (typeof data?.error === 'string' && data.error.trim()) return data.error;
  return fallback;
};

export const setBotTokens = (accessToken: string, refreshToken?: string, userId?: string) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
  if (userId) {
    localStorage.setItem(BOT_USER_ID_KEY, userId);
  }
};

export const clearBotTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(BOT_USER_ID_KEY);
};

export const getBotAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY) || '';
export const getBotRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY) || '';

export const loginToBotRuntime = async (userId: string) => {
  const response = await fetch(buildBotUrl('/auth/login'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),

  });
  // console.log("-------------",response  );


  const data = await parseJsonSafely(response);
  if (!response.ok) {
    throw new Error(getErrorMessage(data, `Bot login failed. HTTP ${response.status}`));
  }
  // console.log("Response object:", response);
  // console.log("Actual data:", data);

  const result = data as BotLoginResponse;
  if (!result?.access_token) {
    throw new Error('Bot login did not return an access token');
  }

  setBotTokens(result.access_token, result.refresh_token, userId);
  return result;
};


export const refreshBotRuntimeToken = async () => {
  const refreshToken = getBotRefreshToken();
  if (!refreshToken) {
    throw new Error('No bot refresh token available');
  }

  const response = await fetch(buildBotUrl('/auth/refresh'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  const data = await parseJsonSafely(response);
  if (!response.ok) {
    clearBotTokens();
    throw new Error(getErrorMessage(data, `Bot token refresh failed. HTTP ${response.status}`));
  }

  const result = data as BotRefreshResponse;
  if (!result?.access_token) {
    clearBotTokens();
    throw new Error('Bot refresh did not return an access token');
  }

  const currentUserId = localStorage.getItem(BOT_USER_ID_KEY) || undefined;
  setBotTokens(result.access_token, undefined, currentUserId);
  return result.access_token;
};

// The bot access token is a JWT with a 60 minute lifetime. WebSocket handshakes are
// rejected outright once it expires, so check the expiry instead of trusting whatever
// happens to be in localStorage.
const isTokenUsable = (token: string, skewSeconds = 120) => {
  const payload = token.split('.')[1];
  if (!payload) return false;

  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = JSON.parse(json)?.exp;
    if (typeof exp !== 'number') return true; // no expiry claim: nothing to check
    return exp - skewSeconds > Date.now() / 1000;
  } catch {
    return false;
  }
};

export const ensureBotRuntimeAuth = async (userId: string) => {
  const existingAccessToken = getBotAccessToken();
  const storedUserId = localStorage.getItem(BOT_USER_ID_KEY);

  if (existingAccessToken && storedUserId === userId) {
    if (isTokenUsable(existingAccessToken)) {
      return existingAccessToken;
    }

    // Expired or about to expire: refresh in place before falling back to a full login.
    try {
      const refreshedToken = await refreshBotRuntimeToken();
      if (refreshedToken && isTokenUsable(refreshedToken)) {
        return refreshedToken;
      }
    } catch (error) {
      console.warn('Bot token refresh failed, logging in again:', error);
    }
  }

  clearBotTokens();
  const loginResponse = await loginToBotRuntime(userId);
  return loginResponse.access_token;
};

export const botRuntimeFetch = async (path: string, options: RequestInit = {}, userId?: string): Promise<Response> => {
  let accessToken = getBotAccessToken();
  if (!accessToken) {
    if (!userId) throw new Error('Bot runtime userId is required to authenticate');
    accessToken = await ensureBotRuntimeAuth(userId);
  }

  const makeRequest = async (token: string) => {
    const headers = new Headers(options.headers || {});
    if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }
    headers.set('Authorization', `Bearer ${token}`);

    return fetch(buildBotUrl(path), {
      ...options,
      headers,
    });
  };

  let response = await makeRequest(accessToken);
  if (response.status !== 401) return response;

  if (!userId) return response;

  try {
    const refreshedToken = await refreshBotRuntimeToken();
    response = await makeRequest(refreshedToken);
  } catch {
    clearBotTokens();
    const loginResponse = await loginToBotRuntime(userId);
    response = await makeRequest(loginResponse.access_token);
  }

  return response;
};

export const botRuntimePostJson = async <T>(path: string, payload: unknown, userId: string): Promise<T> => {
  const response = await botRuntimeFetch(path, {
    method: 'POST',
    body: JSON.stringify(payload),
  }, userId);

  const data = await parseJsonSafely(response);
  if (!response.ok) {
    throw new Error(getErrorMessage(data, `Bot runtime request failed. HTTP ${response.status}`));
  }

  return data as T;
};

export const startBotRuntimeAutomation = async (payload: {
  user_id: string;
  task_id: string;
  dataset?: unknown;
}) => botRuntimePostJson<{ success?: boolean; message?: string }>('/automation-runner/prompt-instruction', payload, payload.user_id);

export const startRpaRuntimeAutomation = async (payload: {
  user_id: string;
  rpa_id: string;
}) => botRuntimePostJson<{ success?: boolean; message?: string }>('/automation_runner/rpa_instruction', payload, payload.user_id);

export const stopBotRuntimeAutomation = async (payload: {
  user_id: string;
  select_id: string;
}) => botRuntimePostJson<{ success?: boolean; message?: string }>('/automation-runner/stop', payload, payload.user_id);

export const stopRpaRuntimeAutomation = async (payload: {
  user_id: string;
  rpa_id: string;
}) => {
  // console.log('🛑 [RPA STOP] Attempting to stop RPA task:', payload);
  try {
    // Convert rpa_id to select_id to match backend StopRequest model
    const requestPayload = {
      user_id: payload.user_id,
      select_id: payload.rpa_id, // Backend expects select_id
    };
    // console.log('🛑 [RPA STOP] Sending request payload:', requestPayload);
    const result = await botRuntimePostJson<{ success?: boolean; message?: string }>('/rpa_automation_runner/stop', requestPayload, payload.user_id);
    // console.log('✅ [RPA STOP] Successfully stopped RPA task:', result);
    return result;
  } catch (error) {
    console.error('❌ [RPA STOP] Failed to stop RPA task:', error);
    throw error;
  }
};

export const getAutomationLogWsUrl = async (taskId: string, userId: string) => {
  const token = await ensureBotRuntimeAuth(userId);
  const wsBase = Botservice.replace(/^https/, 'wss').replace(/^http/, 'ws');
  return `${wsBase}ws/asst-ui-automation/${taskId}/${userId}?token=${encodeURIComponent(token)}`;
};

export const getRpaLogWsUrl = (rpaId: string, userId: string) => {
  console.log('rpaId',rpaId)
  const wsBase = Botservice.replace(/^https/, 'wss').replace(/^http/, 'ws');
  return `${wsBase}ws/rpa-logs/${rpaId}/${userId}`;
};
